import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { getUniqueObjects } from "../../utilities/utils";

export const getSearchUser = createAsyncThunk(
  "event/getSearchUser",
  async (searchQuery, { rejectWithValue, getState }) => {
    try {
      const response = await axiosInstance.get(
        `v1/Account/getContactsCard?searchString=${searchQuery}`
      );
      const state = getState();
      const eventUserList = state.event.eventAttendeesListForSearch?.map((res) => ({...res, id: res?.contact_ID}));
      const keysToSearch = ["email", "bidderNumber", "displayName", "firstName", "lastName"];
      const searchResults = eventUserList.filter(item =>
        keysToSearch.some(key =>
          item[key]?.toString().toLowerCase().includes(searchQuery?.trim()?.toLowerCase())
        )
      );

      const data = response?.data?.contacts;
      let mergedData = [...searchResults, ...data]
      if(searchResults.length) {
        mergedData = getUniqueObjects(mergedData, 'id', 'bidderNumber' )
      }
      const finalData = { contacts: mergedData}

      return finalData;
    } catch (err) {
      console.log('err', err)
      return rejectWithValue(
        err.response?.data?.message || "Error fetching users"
      );
    }
  }
);

export const getUserData = createAsyncThunk(
  "user/getUserData",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/v1/contact/Get?ID=${userId}`);
      const accountId = response?.data?.accounts.find(
        (res) => !res.isBusinessAccount
      )?.account_ID;
      const newResponse = await axiosInstance.get(`/profiles/${accountId}`, {
        meta: { base: "contact_api" },
      });
      const companyResponse = await axiosInstance.get(
        `/Profile/GetAllAccountsPerContact?ContactId=${userId}`,
        {
          meta: { base: "company_api" },
        }
      );

      const data = {
        ...response?.data,
        ...newResponse?.data,
        accounts: companyResponse?.data,
      };
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error fetching user data"
      );
    }
  }
);

export const getExistingUsers = createAsyncThunk(
  "event/getExistingUsers",
  async (searchQuery, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `v1/Account/getContactsCard?searchString=${searchQuery}`
      );
      const data = response?.data?.contacts;
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error fetching users"
      );
    }
  }
);

export const addUser = createAsyncThunk(
  "company/addUser",
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/v1/account/createPersonalAccount`,
        payload
      );
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error creating User"
      );
    }
  }
);

export const checkDuplicateEmail = createAsyncThunk(
  "user/checkDuplicateEmail",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/v1/Account/checkDuplicateEmail?email=${encodeURIComponent(email)}`
      );
      return response?.data;
    } catch (err) {
      // Soft failure: if the precheck call errors out (network, auth, etc.) just let the
      // operator continue through the wizard. There's no authoritative backend guard, so the
      // worst case is the same as today — a possible duplicate. Better than a hard-blocking UI.
      return rejectWithValue(
        err.response?.data?.message || "Error checking duplicate email"
      );
    }
  }
);

export const addUserWithBusinessAccount = createAsyncThunk(
  "company/addUserWithBusinessAccount",
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `v1/Account/addBusinessAccount`,
        payload
      );
      const data = response?.data;
      onSuccess?.(data);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error creating User"
      );
    }
  }
);

export const getContactFlag = createAsyncThunk(
  "event/getContactFlag",
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `profile/GetAllAccountsPerContact?ContactId=${contactId}`,
        {
          meta: { base: "company_api" },
        }
      );
      const data = response?.data;
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error fetching users"
      );
    }
  }
);

export const getUserDataSilencly = createAsyncThunk(
  "user/getUserDataSilencly",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/v1/contact/Get?ID=${userId}`);
      const accountId = response?.data?.accounts.find(
        (res) => !res.isBusinessAccount
      )?.account_ID;
      const newResponse = await axiosInstance.get(`/profiles/${accountId}`, {
        meta: { base: "contact_api" },
      });
      const data = {
        ...response?.data,
        ...newResponse?.data,
      };
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error fetching user data"
      );
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    searchResults: [],
    showResults: false,
    status: "idle",
    error: null,
    loading: false,
    selectedDashboardTab: "pd",
    selectedUser: null,
    selectedUserData: null,
    tabUsers: [],
    searchExistingUser: [],
    searchExistingLoading: false,
    addUserError: null,
    addUserLoading: false,
    addUserRes: null,
    contactFlagLoading: false,
    contactFlagRes: null,
    contactFlagError: null,
    refetchLoading: false,
  },
  reducers: {
    resetSelectedUser: (state) => {
      state.selectedUser = null;
      state.selectedUserData = null;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },
    setShowResults: (state, action) => {
      state.showResults = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.showResults = false;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    addUserToTab: (state, action) => {
      if (
        state.tabUsers.some(
          (user) => user.contact_ID === action.payload.contact_ID
        )
      ) {
        return;
      }
      state.tabUsers.push(action.payload);
    },
    setTabUserName: (state, action) => {
      const payload = action.payload;
      state.tabUsers = state.tabUsers.map((user) => {
        if (user.contact_ID == payload.id) {
          return {
            ...user,
            name: payload?.name,
          };
        } else {
          return user;
        }
      });
    },
    removeUserFromTab: (state, action) => {
      state.tabUsers = state.tabUsers.filter(
        (user) => user.contact_ID !== action.payload
      );
    },
    setSearchExistingUser: (state, action) => {
      state.searchExistingUser = action.payload;
    },
    setSelectedDashboardTab: (state, action) => {
      state.selectedDashboardTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSearchUser.pending, (state) => {
        state.selectedUserData = null;
        state.loading = true;
      })
      .addCase(getSearchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload?.contacts;
        state.showResults = true;
      })
      .addCase(getSearchUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getUserData.pending, (state) => {
        state.selectedUserData = null;
        state.status = "loading";
        state.loading = true;
      })
      .addCase(getUserData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedUserData = action.payload;
        state.loading = false;
      })
      .addCase(getUserData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getExistingUsers.pending, (state) => {
        state.searchExistingLoading = true;
      })
      .addCase(getExistingUsers.fulfilled, (state, action) => {
        state.searchExistingUser = action.payload;
        state.searchExistingLoading = false;
      })
      .addCase(getExistingUsers.rejected, (state, action) => {
        state.searchExistingLoading = false;
      })

      .addCase(addUser.pending, (state) => {
        state.addUserLoading = true;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.addUserLoading = false;
        state.addUserRes = action.payload;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.addUserError = action.payload;
        state.addUserLoading = false;
      })

      .addCase(addUserWithBusinessAccount.pending, (state) => {
        state.addUserLoading = true;
      })
      .addCase(addUserWithBusinessAccount.fulfilled, (state, action) => {
        state.addUserLoading = false;
        state.addUserRes = action.payload;
      })
      .addCase(addUserWithBusinessAccount.rejected, (state, action) => {
        state.addUserError = action.payload;
        state.addUserLoading = false;
      })

      .addCase(getContactFlag.pending, (state) => {
        state.contactFlagLoading = true;
      })
      .addCase(getContactFlag.fulfilled, (state, action) => {
        state.contactFlagLoading = false;
        state.contactFlagRes = action.payload;
      })
      .addCase(getContactFlag.rejected, (state, action) => {
        state.contactFlagError = action.payload;
        state.contactFlagLoading = false;
      })
      .addCase(getUserDataSilencly.pending, (state) => {
        state.refetchLoading = true;
      })
      .addCase(getUserDataSilencly.fulfilled, (state, action) => {
        state.refetchLoading = false;
        state.selectedUserData = {
          ...state.selectedUserData,
          ...action.payload,
          accounts: state.selectedUserData?.accounts
        };
      })
      .addCase(getUserDataSilencly.rejected, (state, action) => {
        state.refetchLoading = false;
      });
  },
});

export const {
  setSelectedUser,
  addUserToTab,
  removeUserFromTab,
  setSearchExistingUser,
  setSelectedDashboardTab,
  setTabUserName,
  resetSelectedUser
} = userSlice.actions;
export default userSlice.reducer;
